import { StoreIcon } from '@/components/icons';
import { Button, Card, Form, Input, Result, Typography } from 'antd';
import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { counterAccess, isAdmin, type CounterAccess } from './access';

import { AppSplash, PageLoader } from '@/components/Loader';
import { useActivePharmacy } from './active-pharmacy';
import { usePharmacyFeatures } from './features';
import { useAuth } from './auth-context';
import type { Permission } from './permissions';

const { Text } = Typography;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;


export function RequireAuth() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) return <AppSplash message="Checking your sign-in" />;
  if (!isAuthenticated) {
    // Carries where they were headed so sign-in can put them back there.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  /*
   * A restored session is checked the same way sign-in is: a branch can be
   * unlinked while somebody is signed in, and a session from before this
   * check existed may have no branch at all.
   */
  const access = user ? counterAccess(user) : 'ok';
  if (access !== 'ok') return <SignOutRefused access={access} />;
  return <Outlet />;
}

/** Ends a session this app cannot serve and says why on the login page. */
function SignOutRefused({ access }: { access: Exclude<CounterAccess, 'ok'> }) {
  const { signOut } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    void signOut().finally(() => navigate('/login', { replace: true, state: { refused: access } }));
  }, [access, navigate, signOut]);

  return <AppSplash message="Signing you out" />;
}

export function RedirectIfAuthenticated({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <AppSplash message="Checking your sign-in" />;
  if (isAuthenticated) return <Navigate to="/" replace />;
  return children;
}

/**
 * Keeps a user off a page whose every request would 403. The server still
 * enforces this - the guard exists so the failure is a clear message instead of
 * a screen of red alerts.
 */
export function RequirePermission({
  permission,
  children,
}: {
  permission: Permission;
  children: ReactNode;
}) {
  const { can } = useAuth();

  if (!can(permission)) {
    return (
      <Result
        status="403"
        title="Not permitted"
        subTitle={`This needs the ${permission} permission. Ask the pharmacist in charge to grant it.`}
      />
    );
  }
  return children;
}

/**
 * Keeps a branch off a page it was never given.
 *
 * The navigation already hides these, but a bookmark, a browser back button or
 * a link in a message does not go through the navigation. The server refuses
 * the requests underneath regardless - this exists so the answer is a sentence
 * rather than a screen of red alerts.
 */
export function RequireFeature({
  feature,
  title,
  children,
}: {
  feature: 'marketplace' | 'prescriptions';
  title: string;
  children: ReactNode;
}) {
  const features = usePharmacyFeatures();

  // Nothing is blocked while the entitlement is still loading: flashing a
  // refusal at someone who is in fact entitled is worse than a moment's wait.
  if (features.isLoading) return <PageLoader label="Checking what this branch has switched on" />;

  const allowed = feature === 'marketplace' ? features.marketplace : features.prescriptions;
  if (allowed) return children;

  return (
    <Result
      icon={<StoreIcon />}
      title={`${title} is not switched on for this branch`}
      subTitle="An administrator decides which parts of the platform a pharmacy takes part in. Ask them to enable it if this looks wrong."
    />
  );
}

/**
 * Every screen in this app belongs to one branch. Staff get theirs from their
 * assignment, so this almost never renders - it exists for the account that has
 * none, which is either an admin visiting or a staff record that was created
 * before its branch was linked.
 */
export function RequirePharmacy({ children }: { children: ReactNode }) {
  const { pharmacyId } = useActivePharmacy();
  if (!pharmacyId) return <NoPharmacyAssigned />;
  return children;
}

function NoPharmacyAssigned() {
  const { user, can } = useAuth();
  const { setOverride } = useActivePharmacy();
  const [submitted, setSubmitted] = useState(false);

  return (
    <div className="mx-auto max-w-lg py-6">
      <Result
        icon={<StoreIcon />}
        title="No pharmacy linked to this account"
        subTitle={
          <span>
            {user?.email ?? 'This account'} is signed in, but it is not registered as staff at any
            branch - so there is no stock, no listings and no counter to show. An admin links a
            branch to a staff account; until then this app has nothing to work on.
          </span>
        }
      />

      {/*
       * Offered only to accounts that can read pharmacies at all - an admin
       * checking a branch. Staff never see it, so nobody is invited to type an
       * id they should have been assigned. The server checks access regardless.
       */}
      {isAdmin(user) && can('pharmacy:read') ? (
        <Card size="small" title="Open a branch by id">
          <Form
            layout="vertical"
            onFinish={(values: { pharmacyId: string }) => {
              setOverride(values.pharmacyId.trim());
              setSubmitted(true);
            }}
          >
            <Form.Item
              name="pharmacyId"
              label="Pharmacy id"
              className="mb-3!"
              rules={[
                { required: true, message: 'Paste a pharmacy id' },
                { pattern: UUID_PATTERN, message: 'That is not a UUID' },
              ]}
            >
              <Input placeholder="00000000-0000-0000-0000-000000000000" allowClear />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={submitted}>
              Open
            </Button>
            <Text type="secondary" className="mt-3 block text-xs leading-relaxed">
              For administrators only, and only until <code>GET /pharmacies</code> stops returning
              501. Access is still checked server-side on every request.
            </Text>
          </Form>
        </Card>
      ) : null}
    </div>
  );
}
